import React from 'react';

const Blog = ({ data, onArticleClick }) => {
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const handleArticleClick = (post) => {
        // Scroll to top smoothly
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });

        // Call the parent handler
        onArticleClick(post);
    };

    return (
        <section className="py-20 bg-white min-h-screen">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-stone-800 mb-6">
                        Blog & Insights
                    </h2>
                    <p className="text-lg text-stone-600 max-w-3xl mx-auto leading-relaxed">
                        Explore articles, insights, and practical guidance on mental health, wellness, and personal growth.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                    {data.map((post) => (
                        <article
                            key={post.id}
                            className="bg-stone-50 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow duration-300 border border-stone-100"
                        >
                            {/* Featured Image */}
                            <div className="w-full h-48 bg-gradient-to-br from-stone-200 to-stone-300">
                                {post.featuredImage ? (
                                    <img
                                        src={post.featuredImage}
                                        alt={post.title}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <div className="text-center">
                                            <div className="w-12 h-12 bg-stone-400 rounded-lg flex items-center justify-center mx-auto mb-2">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                            <p className="text-xs text-stone-600">Add image in admin</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="inline-block bg-amber-100 text-amber-700 text-xs font-medium px-3 py-1 rounded-full">
                                        {post.category}
                                    </span>
                                    <span className="text-stone-500 text-sm">
                                        {formatDate(post.date)}
                                    </span>
                                </div>

                                <h3 className="text-xl font-semibold text-stone-800 mb-3 leading-tight">
                                    {post.title}
                                </h3>

                                <p className="text-stone-600 mb-4 leading-relaxed">
                                    {post.excerpt}
                                </p>

                                <button
                                    onClick={() => handleArticleClick(post)}
                                    className="text-stone-700 font-medium hover:text-stone-900 transition-colors duration-200 border-b border-stone-300 hover:border-stone-500"
                                >
                                    Read More
                                </button>
                            </div>
                        </article>
                    ))}
                </div>

                {/* Featured Article */}
                {data.length > 0 && (
                    <div className="bg-gradient-to-r from-stone-50 to-amber-50/30 rounded-2xl p-8 lg:p-12 mb-16">
                        <div className="lg:flex lg:items-center lg:space-x-12">
                            <div className="lg:w-1/2 mb-8 lg:mb-0">
                                <div className="w-full h-64 bg-gradient-to-br from-stone-200 to-stone-300 rounded-xl overflow-hidden">
                                    {data[0].featuredImage ? (
                                        <img
                                            src={data[0].featuredImage}
                                            alt={data[0].title}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <div className="text-center">
                                                <div className="w-16 h-16 bg-stone-400 rounded-xl flex items-center justify-center mx-auto mb-3">
                                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                    </svg>
                                                </div>
                                                <p className="text-sm text-stone-600">Featured Article Image</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="lg:w-1/2">
                                <div className="inline-block bg-amber-200 text-amber-800 text-sm font-medium px-4 py-2 rounded-full mb-4">
                                    Featured Article
                                </div>
                                <h3 className="text-2xl sm:text-3xl font-light text-stone-800 mb-4 leading-tight">
                                    {data[0].title}
                                </h3>
                                <p className="text-stone-600 mb-6 leading-relaxed">
                                    {data[0].excerpt}
                                </p>
                                <button
                                    onClick={() => handleArticleClick(data[0])}
                                    className="bg-stone-800 text-white px-6 py-3 rounded-lg font-medium hover:bg-stone-700 transition-colors duration-200"
                                >
                                    Read Full Article
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Newsletter Signup */}
                <div className="bg-stone-800 rounded-2xl p-8 lg:p-12 text-center text-white">
                    <h3 className="text-2xl sm:text-3xl font-light mb-4">
                        Stay Updated
                    </h3>
                    <p className="text-stone-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                        Subscribe to receive the latest articles, insights, and wellness tips directly in your inbox.
                    </p>

                    <div className="max-w-md mx-auto flex flex-col sm:flex-row gap-4">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-4 py-3 rounded-lg text-stone-800 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                        />
                        <button className="bg-amber-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-amber-600 transition-colors duration-200">
                            Subscribe
                        </button>
                    </div>

                    <p className="text-stone-400 text-xs mt-4">
                        No spam, unsubscribe at any time
                    </p>
                </div>

                {/* Blog Categories */}
                <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white p-6 rounded-xl border border-stone-100 text-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                        </div>
                        <h4 className="font-semibold text-stone-800 mb-1">Anxiety & Stress</h4>
                        <p className="text-stone-600 text-sm">12 articles</p>
                    </div>

                    <div className="bg-white p-6 rounded-xl border border-stone-100 text-center">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </div>
                        <h4 className="font-semibold text-stone-800 mb-1">Relationships</h4>
                        <p className="text-stone-600 text-sm">8 articles</p>
                    </div>

                    <div className="bg-white p-6 rounded-xl border border-stone-100 text-center">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <h4 className="font-semibold text-stone-800 mb-1">Personal Growth</h4>
                        <p className="text-stone-600 text-sm">15 articles</p>
                    </div>

                    <div className="bg-white p-6 rounded-xl border border-stone-100 text-center">
                        <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </div>
                        <h4 className="font-semibold text-stone-800 mb-1">Mindfulness</h4>
                        <p className="text-stone-600 text-sm">10 articles</p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Blog;